import React, { useMemo } from "react";
import { subDays, subWeeks } from "date-fns";
import { useConstant } from "@hooks/useConstant";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    getWeekRange,
    parseDateOnly,
    toDateOnly,
} from "@design-system/DatePicker/DatePicker.lib";
import type { DatePickerPreset } from "@design-system/DatePicker/DatePicker.types";
import { DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";

interface Props {
    /** The selected as-of date, or undefined to default server-side to today. */
    value: Date | undefined;
    onChange: (value: Date | undefined) => void;
}

/**
 * AR Aging is always as of a single point-in-time date, so every period
 * preset (This month, Last quarter, etc.) resolves to the LAST day of that
 * period rather than offering a range — mirrors what QBO does, and what the
 * relative-range presets elsewhere in the app (`DATE_RANGE_PRESETS_BY_ID` in
 * `@helpers/date`) already compute as their `getEnd`. Today/Yesterday and
 * This/Last week use `DatePicker.lib`'s own week helper the same way
 * `getDefaultRangePresets` does, since `@helpers/date` doesn't define a
 * "last_week" preset.
 */
export function buildArAgingAsOfDatePresets(
    today: Date,
): ReadonlyArray<DatePickerPreset<string>> {
    const periodEndPresets: Array<{ label: string; presetId: string }> = [
        { label: "This month", presetId: "this_month" },
        { label: "Last month", presetId: "last_month" },
        { label: "This quarter", presetId: "this_quarter" },
        { label: "Last quarter", presetId: "last_quarter" },
        { label: "This year", presetId: "this_year" },
        { label: "Last year", presetId: "last_year" },
    ];

    return [
        { label: "Today", value: toDateOnly(today) ?? "" },
        { label: "Yesterday", value: toDateOnly(subDays(today, 1)) ?? "" },
        {
            label: "This week",
            value: toDateOnly(getWeekRange(today, 0).to) ?? "",
        },
        {
            label: "Last week",
            value: toDateOnly(getWeekRange(subWeeks(today, 1), 0).to) ?? "",
        },
        ...periodEndPresets.flatMap(({ label, presetId }) => {
            const preset = DATE_RANGE_PRESETS_BY_ID[presetId];
            if (!preset) {
                return [];
            }
            return [{ label, value: toDateOnly(preset.getEnd(today)) ?? "" }];
        }),
    ];
}

/**
 * As-of-date picker for the A/R Aging Summary + Detail reports.
 *
 * AR Aging takes one calendar date (not a start/end range), so it cannot
 * reuse `DateRangeFilterNext`, which always renders `DatePicker` in
 * `mode="range"`. `DatePicker` already supports a single-date mode
 * (`mode="day"`, used elsewhere for one-off date pickers like
 * `ConfirmInvoiceStatusDateModal`), so this wraps that directly rather than
 * inventing a new design-system component. The default Today/Yesterday
 * presets (`getDefaultSinglePresets`) are extended with the same relative
 * period presets other accounting reports offer (This/Last week, month,
 * quarter, year), each resolved to the last day of that period since AR
 * aging is always a single point-in-time snapshot. Picking any date
 * directly from the calendar covers the "custom" case. There is no
 * `usePinnedDatePresets`-equivalent for single dates in the codebase (that
 * hook's cache schema and preset catalog are range-only), so pinning is
 * skipped here.
 */
export const ArAgingAsOfDateFilter: React.FC<Props> = ({ value, onChange }) => {
    const today = useConstant(() => new Date());
    const presets = useMemo(() => buildArAgingAsOfDatePresets(today), [today]);

    return (
        <DatePicker
            mode="day"
            variant="button"
            clearable={false}
            showPresets
            presets={presets}
            value={toDateOnly(value)}
            onValueChange={(next) => onChange(parseDateOnly(next))}
            placeholder="As of date"
        />
    );
};
